import { create } from "zustand";
import { persist } from "zustand/middleware";

interface SidebarState {
  // Persisted state
  isAppSidebarOpen: boolean;
  setIsAppSidebarOpen: (open: boolean) => void;
  collapsedGroups: Record<string, boolean>;
  setCollapsedGroups: (groups: Record<string, boolean>) => void;
  toggleGroup: (key: string) => void;
  collapsedSubGroups: Record<string, boolean>;
  setCollapsedSubGroups: (groups: Record<string, boolean>) => void;
  toggleSubGroup: (key: string) => void;

  // Runtime state (not persisted)
  appSidebarVisible: boolean;
  setAppSidebarVisible: (visible: boolean) => void;
  rightSidebarToggleDisabled: boolean;
  setRightSidebarToggleDisabled: (disabled: boolean) => void;
  /**
   * A page that needs the width (the dashboard's widget editor, LAR-726) folds
   * the app sidebar away for a while, without touching the saved preference.
   */
  isAppSidebarYielding: boolean;
  setAppSidebarYielding: (yielding: boolean) => void;
}

const useSidebarStore = create<SidebarState>()(
  persist(
    (set) => ({
      // Persisted state
      isAppSidebarOpen: true,
      setIsAppSidebarOpen: (open: boolean) => set({ isAppSidebarOpen: open }),

      collapsedGroups: {
        app: false,
        admin: false,
        product: false,
      },
      setCollapsedGroups: (groups) => set({ collapsedGroups: groups }),
      toggleGroup: (key) =>
        set((state) => ({
          collapsedGroups: {
            ...state.collapsedGroups,
            [key]: !state.collapsedGroups[key],
          },
        })),

      collapsedSubGroups: {},
      setCollapsedSubGroups: (groups) => set({ collapsedSubGroups: groups }),
      toggleSubGroup: (key) =>
        set((state) => ({
          collapsedSubGroups: {
            ...state.collapsedSubGroups,
            [key]: !state.collapsedSubGroups[key],
          },
        })),

      // Runtime state
      appSidebarVisible: false,
      setAppSidebarVisible: (visible: boolean) =>
        set({ appSidebarVisible: visible }),
      rightSidebarToggleDisabled: false,
      setRightSidebarToggleDisabled: (disabled: boolean) =>
        set({ rightSidebarToggleDisabled: disabled }),
      isAppSidebarYielding: false,
      setAppSidebarYielding: (yielding: boolean) =>
        set({ isAppSidebarYielding: yielding }),
    }),
    {
      name: "sidebar-storage",
      partialize: (state) => ({
        // Only persist these fields
        isAppSidebarOpen: state.isAppSidebarOpen,
        collapsedGroups: state.collapsedGroups,
        collapsedSubGroups: state.collapsedSubGroups,
      }),
    }
  )
);

export default useSidebarStore;
